import { useTranslation } from 'react-i18next'

import type { CampaignContactRow } from '#/entities/campaign'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '#/shared/ui/dropdown-menu'
import { phoneToDialable } from '#/shared/lib/format'
import { MoreHorizontalIcon } from '#/shared/ui/icons'

import type { CampaignContactRowActions } from '../model/use-campaign-contact-row-actions'

/**
 * The bulk bar's actions, aimed at one person.
 *
 * Removing somebody is not put behind a confirm here, because the bar above
 * the table has never asked either and an enrollment can be added back — the
 * destructive ink is the warning, and the toast says how many actually went.
 */
export function CampaignContactRowMenu({
  row,
  actions,
}: {
  row: CampaignContactRow
  actions: CampaignContactRowActions
}) {
  const { t } = useTranslation()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t('campaigns.contact.rowActions', { name: row.name })}
        disabled={actions.busy}
        className="flex size-6 cursor-pointer items-center justify-center rounded-md text-gray-300 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <MoreHorizontalIcon className="size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {/* Nothing to open a thread on when the row carries no number. */}
        <DropdownMenuItem
          disabled={phoneToDialable(row.phone) == null}
          onSelect={() => actions.onOpenInPhone(row)}
        >
          {t('campaigns.contact.rowMenu.openInPhone')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => actions.onAddToSequence(row)}>
          {t('campaigns.contact.rowMenu.addToAnotherSequence')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => actions.onAssign(row)}>
          {t('campaigns.contact.rowMenu.assignToAgent')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => actions.onCreateTask(row)}>
          {t('campaigns.contact.rowMenu.createTask')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          variant="destructive"
          onSelect={() => actions.onRemove(row)}
        >
          {t('campaigns.contact.rowMenu.removeFromSequence')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
